/**
 * Ground scatter (owner, Oct 10: "a little more flora, assuming it's dirt cheap and looks good"):
 * grass tufts, dry grass, low scrub and small stones — and stumps and deadfall in the fir stands,
 * flowers in the broadleaf wood, puddles and dry pans on the low flat ground, scree at the foot of
 * the mountain rocks — around a person-scale battle — thick under the
 * biome stands, sparse on the open ground between them, none on the capture rings. Dirt cheap by
 * construction: **one InstancedMesh per kind** (four draw calls for a thousand pieces), a few
 * triangles each, tinted per instance, placed once; nothing in the sim — not obstacles, not state —
 * so it never moves a checksum. Seeded from the match seed so a replay looks the same.
 *
 * The placement is pure and exported (`scatterPlan`, tests/groundScatter.test.ts); the drawing is
 * presentation only. Human scale only (k_human < 1): the classic view never sees it.
 *
 * The weather on the ground (owner, Oct 10): the grass, the dry grass and the flowers sway in the
 * wind — a vertex bend in the shader, gusts travelling along the wind, a breeze always and a gale in a
 * storm (`WIND`) — and the puddles and pans go dark and glossy in the rain and dry out slowly after
 * (`WET`). Both ease, both are uniforms: no per-frame instance work.
 */
import * as THREE from 'three';
import type { GameState, Vec3, BiomeId, PlanetState, FloraState } from '../types';
import type { ForestKeepOut } from '../core/forest';
import type { NearFieldStreamer, NearFieldClient } from './nearField';
import type { WeatherKind } from '../core/weather';
import { SeededRandom } from '../core/random';
import { dsin, dcos, dacos } from '../core/dmath';
import { getSurfaceRadius } from '../core/surface';
import { terraceReach } from '../core/terraceGround';
import { CRATERLET_REACH } from '../core/craterlets';
import { FINE_FADE } from './fineTerrainPatch';
import { flatDiscs, flatFactor } from './fineFlatten';
import { GROUND_LIFT, fineGroundLift } from './groundLift';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { BIOME_COLORS } from './biomeKit';
import { markShared } from './meshBake';
import { windSwayUniforms, stepWindSway, WIND_SWAY, WIND_SWAY_HEAD, WIND_FRONT_GLSL, type WindSwayUniforms } from './windSway';

export type ScatterKind = 'grass' | 'dryGrass' | 'scrub' | 'stone' | 'stump' | 'deadfall' | 'flower' | 'pan';
export const SCATTER_KINDS: ScatterKind[] = ['grass', 'dryGrass', 'scrub', 'stone', 'stump', 'deadfall', 'flower', 'pan'];

/** A piece: where (a unit direction on its planet), how turned, how big (× the kind's size), which tint (0–1). */
export interface ScatterPiece { kind: ScatterKind; planetIndex: number; dir: Vec3; yaw: number; scale: number; tint: number }

/** Pieces per stand tree, per stand rock, and per open-ground spot; the whole battle's cap. */
export const SCATTER = { perTree: 6, perRock: 3, perOutcrop: 7, perCluster: 2, openSpots: 120, perOpen: 2, pans: 24, max: 1600, standRadius: 3.5, openRadius: 4 };   // radii in people
/**
 * 67.46 step 2: a site wider than `wide` people across (a board, its wood in patches) shares the scatter: past what the
 * stands can take (the cap less the open ground's), every tree and rock gets the same smaller share, not the first ones
 * all of it; and the open ground and the pans are looked for within `near` people of the wood — each patch's own ground
 * — rather than across the whole board. A site the Lab's size is as it was.
 */
export const SCATTER_SHARED = { wide: 300, near: 30 };
/** Puddles and dry pans: on the lowest share of the area's sampled heights, on ground flatter than this. */
export const PAN = { lowShare: 0.25, maxSlope: 0.05 };

/** What grows where (weights per biome); the open ground between stands is sparse — dry grass and stones. */
const MIX: Record<BiomeId | 'open', Array<[ScatterKind, number]>> = {
  broadleaf: [['grass', 5], ['scrub', 1], ['stone', 1], ['flower', 2]],
  firStand: [['grass', 3], ['scrub', 2], ['stone', 2], ['stump', 1], ['deadfall', 1]],
  heath: [['dryGrass', 4], ['scrub', 2], ['stone', 2]],
  savanna: [['dryGrass', 5], ['scrub', 1], ['stone', 2]],
  ashBarrens: [['stone', 5], ['dryGrass', 1], ['deadfall', 1]],
  lanternGrove: [['grass', 3], ['scrub', 1], ['stone', 2], ['flower', 1]],
  birchGlade: [['grass', 5], ['flower', 3], ['stone', 1]],
  burn: [['stump', 3], ['deadfall', 3], ['stone', 2], ['dryGrass', 1]],
  scrub: [['dryGrass', 4], ['scrub', 3], ['stone', 2]],
  hollowWood: [['stone', 3], ['grass', 1], ['deadfall', 1]],
  open: [['dryGrass', 2], ['stone', 2]],
};

const norm = (v: Vec3): Vec3 => { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };
const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, dot(a, b))));
function pick<T>(list: Array<[T, number]>, rng: SeededRandom): T {
  const total = list.reduce((a, [, w]) => a + w, 0);
  let x = rng.range(0, total);
  for (const [v, w] of list) { x -= w; if (x <= 0) return v; }
  return list[list.length - 1][0];
}
/** A direction within `angle` of `c`. */
function inCap(c: Vec3, angle: number, rng: SeededRandom): Vec3 {
  const u = rng.range(dcos(angle), 1), th = dacos(u), ph = rng.range(0, Math.PI * 2);
  const ref = Math.abs(c.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const t1 = norm(cross(c, ref)), t2 = norm(cross(c, t1));
  const s = dsin(th), k = dcos(th), cp = dcos(ph), sp = dsin(ph);
  return norm({ x: c.x * k + (t1.x * cp + t2.x * sp) * s, y: c.y * k + (t1.y * cp + t2.y * sp) * s, z: c.z * k + (t1.z * cp + t2.z * sp) * s });
}

/**
 * Where the scatter goes: SCATTER.perTree pieces round every normal-size tree and perRock round every
 * normal rock (in the stand's biome), then open-ground spots across the battle area in the nearest
 * stand's biome (or sparse 'open' past 30 people from any), never on a capture ring or on a unit.
 * Empty at the classic scale.
 */
export function scatterPlan(state: GameState, seed: string): ScatterPiece[] {
  const ps = personSetOf(state);
  if (ps.k >= 1) return [];
  const person = PERSON_BODY_HEIGHT * ps.k;
  const rng = new SeededRandom(`${seed}-scatter`);
  const out: ScatterPiece[] = [];
  const capture = state.config.modeConfig.captureRadius ?? ps.captureRadius;
  const planets = [...new Set(state.players.filter(p => p.alive).map(p => p.planetIndex))].sort((a, b) => a - b);
  for (const pi of planets) {
    const planet = state.planets[pi];
    if (!planet) continue;
    const P = person / planet.radius, capA = (capture * 1.1) / planet.radius;
    const sites = (state.modeState.points?.sites ?? []).filter(s => s.planetIndex === pi).map(s => norm(s.surfaceDir));
    const units = state.players.filter(p => p.planetIndex === pi && p.alive).map(p => norm(p.surfaceDir));
    const stands = [
      ...state.flora.filter(f => f.planetIndex === pi && f.size === 'normal' && f.alive).map(f => ({ dir: norm(f.surfaceDir), biome: f.biome ?? 'broadleaf', n: SCATTER.perTree })),
      // scree: densest at the foot of the mountain rocks, lighter among a field's cobbles
      ...state.rocks.filter(r => r.planetIndex === pi && r.size === 'normal' && r.alive).map(r => ({ dir: norm(r.surfaceDir), biome: r.biome ?? 'broadleaf', n: r.mode === 'outcrop' ? SCATTER.perOutcrop : r.mode === 'cluster' ? SCATTER.perCluster : SCATTER.perRock, stones: r.mode === 'outcrop' })),
    ];
    if (stands.length === 0) continue;
    const clear = (d: Vec3) => sites.every(s => arc(d, s) > capA) && units.every(u => arc(d, u) > 3 * P);
    const place = (d: Vec3, biome: BiomeId | 'open') => {
      if (!clear(d) || out.length >= SCATTER.max) return;
      out.push({ kind: pick(MIX[biome], rng), planetIndex: pi, dir: d, yaw: rng.range(0, Math.PI * 2), scale: rng.range(0.7, 1.3), tint: rng.range(0, 1) });
    };
    // 67.46 step 2: past what the stands can take, each its share (stochastically rounded), so the last patch has ground too
    const room = SCATTER.max - SCATTER.openSpots * SCATTER.perOpen - out.length, want = stands.reduce((a, s) => a + s.n, 0);
    if (want > room && room > 0) {
      const share = room / want;
      for (const s of stands) { const x = s.n * share, f = Math.floor(x); s.n = f + (rng.range(0, 1) < x - f ? 1 : 0); }
    }
    for (const s of stands) for (let i = 0; i < s.n; i++) {
      const d = inCap(s.dir, SCATTER.standRadius * P, rng);
      if ('stones' in s && s.stones) { if (clear(d) && out.length < SCATTER.max) out.push({ kind: 'stone', planetIndex: pi, dir: d, yaw: rng.range(0, Math.PI * 2), scale: rng.range(0.6, 1.4), tint: rng.range(0, 1) }); }
      else place(d, s.biome);
    }
    // the open ground: round the stands' centre, as wide as they spread
    let c = stands.reduce((a, s) => ({ x: a.x + s.dir.x, y: a.y + s.dir.y, z: a.z + s.dir.z }), { x: 0, y: 0, z: 0 });
    c = Math.hypot(c.x, c.y, c.z) > 1e-9 ? norm(c) : stands[0].dir;
    const spread = stands.reduce((m, s) => Math.max(m, arc(c, s.dir)), 0) * 1.1 + 20 * P;
    // 67.46 step 2: on a wide site the open ground is each patch's, round the wood; on the Lab's, round the whole site
    const wide = spread * 2 > SCATTER_SHARED.wide * P;
    const nearWood = () => inCap(stands[Math.min(stands.length - 1, Math.floor(rng.range(0, stands.length)))].dir, SCATTER_SHARED.near * P, rng);
    for (let i = 0; i < SCATTER.openSpots; i++) {
      const spot = wide ? nearWood() : inCap(c, spread, rng);
      let near: typeof stands[number] | null = null, best = Infinity;
      for (const s of stands) { const a = arc(spot, s.dir); if (a < best) { best = a; near = s; } }
      const biome: BiomeId | 'open' = near && best < 30 * P ? near.biome : 'open';
      for (let k = 0; k < SCATTER.perOpen; k++) place(inCap(spot, SCATTER.openRadius * P, rng), biome);
    }
    // puddles and dry pans: the lowest, flattest ground in the area (sampled), a few people across
    const probes: Vec3[] = [], heights: number[] = [];
    for (let i = 0; i < 90; i++) { const d = wide ? nearWood() : inCap(c, spread, rng); probes.push(d); heights.push(getSurfaceRadius(planet, d) - planet.radius); }
    const sorted = [...heights].sort((x, y) => x - y), lowBelow = sorted[Math.floor(sorted.length * PAN.lowShare)];
    let pans = 0;
    for (let i = 0; i < probes.length && pans < SCATTER.pans; i++) {
      const d = probes[i];
      if (heights[i] > lowBelow || slopeAt(planet, d, 3 * person) > PAN.maxSlope || !clear(d)) continue;
      pans++;
      out.push({ kind: 'pan', planetIndex: pi, dir: d, yaw: rng.range(0, Math.PI * 2), scale: rng.range(2, 4), tint: rng.range(0, 1) });
    }
  }
  return out;
}

/**
 * 62.61's follow-up / E5.4: the forest cells' own clutter, planet-wide. The scatter streams on the forest's cell grid as
 * its own near-field client — its reach `reachPeople`, much nearer than the trees' 800: whole to 75 people, past the
 * shoulder view's horizon (about 60), a tuft three pixels by 100; at 150 it drew half again the triangles — and
 * a cell's pieces are made from the trees the stands filed for it (`ForestScatterSource`, rendering/treeStands.ts):
 * `perTree` round each in its biome's mix (as the dressed site's), and `openSpots` within `near` people of the wood in the
 * nearest tree's biome (or the sparse open mix), clear of the forest's keep-out. Seeded by the planet's forest seed and
 * the cell: the same on every return and in every battle on the planet (decision 158). Each piece shrinks to nothing over
 * the reach's last quarter from the eye (`shrinkFrom`), so a cell made or dropped is never seen to pop. No pans: they want
 * an area's low ground. Drawn only.
 */
export const SCATTER_STREAM = { reachPeople: 100, shrinkFrom: 0.75, perTree: SCATTER.perTree, openSpots: 4, perOpen: SCATTER.perOpen, near: 30, /** 69.76: a cell made grows in over this (seconds): one made late is no pop */ growS: 0.35 };

/** A forest cell's scatter (pure): round its trees and on the open ground near them, clear of the keep-out. */
export function forestScatterCell(
  planetIndex: number, seed: string, cell: number, trees: readonly Pick<FloraState, 'surfaceDir' | 'biome'>[],
  keepOut: readonly ForestKeepOut[], person: number, radius: number,
): ScatterPiece[] {
  if (trees.length === 0) return [];
  const rng = new SeededRandom(`${seed}-scatter-${planetIndex}-${cell}`);
  const P = person / radius, out: ScatterPiece[] = [];
  const dirs = trees.map(t => norm(t.surfaceDir));
  const clear = (d: Vec3) => keepOut.every(k => arc(d, k.dir) > k.angle);
  const place = (d: Vec3, biome: BiomeId | 'open') => {
    if (!clear(d)) return;
    out.push({ kind: pick(MIX[biome], rng), planetIndex, dir: d, yaw: rng.range(0, Math.PI * 2), scale: rng.range(0.7, 1.3), tint: rng.range(0, 1) });
  };
  for (let t = 0; t < trees.length; t++) for (let i = 0; i < SCATTER_STREAM.perTree; i++) place(inCap(dirs[t], SCATTER.standRadius * P, rng), trees[t].biome ?? 'broadleaf');
  for (let i = 0; i < SCATTER_STREAM.openSpots; i++) {
    const spot = inCap(dirs[Math.min(dirs.length - 1, Math.floor(rng.range(0, dirs.length)))], SCATTER_STREAM.near * P, rng);
    let near = -1, best = Infinity;
    for (let t = 0; t < dirs.length; t++) { const a = arc(spot, dirs[t]); if (a < best) { best = a; near = t; } }
    const biome: BiomeId | 'open' = best < 30 * P ? trees[near].biome ?? 'broadleaf' : 'open';
    for (let k = 0; k < SCATTER_STREAM.perOpen; k++) place(inCap(spot, SCATTER.openRadius * P, rng), biome);
  }
  return out;
}

/** Where the streamed scatter takes a forest cell's trees: the stands, which file them once for the trees' own drawing. */
export interface ForestScatterSource {
  /** The forest's planets and their grids (core/cells.ts cells per face edge). */
  forestGrid(): ReadonlyArray<{ planetIndex: number; n: number }>;
  /** A cell's trees (filed if they were not), the forest's keep-out on its planet and the planet's forest seed; null off the forest. */
  forestCellOf(planetIndex: number, planet: PlanetState, cell: number): { trees: readonly FloraState[]; keepOut: readonly ForestKeepOut[]; seed: string } | null;
}

/** The ground's slope at `d` (rise over run) across `step` world units. */
function slopeAt(planet: PlanetState, d: Vec3, step: number): number {
  const ref = Math.abs(d.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const t1 = norm(cross(d, ref)), t2 = norm(cross(d, t1)), a = step / planet.radius;
  const at = (t: Vec3, s: number) => getSurfaceRadius(planet, norm({ x: d.x + t.x * a * s, y: d.y + t.y * a * s, z: d.z + t.z * a * s }));
  const sx = (at(t1, 1) - at(t1, -1)) / (2 * step), sy = (at(t2, 1) - at(t2, -1)) / (2 * step);
  return Math.sqrt(sx * sx + sy * sy);
}

// ---------------------------------------------------------------------------
// Drawing: one instanced mesh per kind
// ---------------------------------------------------------------------------

/** Each kind's size (× the person's height) and its two tints (the instance tint interpolates). */
const LOOK: Record<ScatterKind, { size: number; a: string; b: string }> = {
  grass: { size: 0.3, a: '#5e7d3e', b: '#86a456' },
  dryGrass: { size: 0.28, a: '#9a8d55', b: '#c4b47a' },
  scrub: { size: 0.4, a: '#4a6338', b: '#6c8a48' },
  stone: { size: 0.16, a: '#6a665e', b: '#8e8a82' },
  stump: { size: 0.3, a: '#5a4635', b: '#7a6248' },
  deadfall: { size: 0.26, a: '#4e3c2c', b: '#6a5440' },
  flower: { size: 0.2, a: '#c89cb8', b: '#e8dca6' },     // soft pinks to creams: colour, kept muted (the brand)
  pan: { size: 1.0, a: '#3c4a46', b: '#5a6860' },       // wet dark to dry pale; the instance tint picks
};

/** Three crossed blades leaning a little: six triangles, darker at the root (vertex colours). */
function bladesGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], col: number[] = [];
  const blade = (angle: number, lean: number) => {
    const c = Math.cos(angle), s = Math.sin(angle), w = 0.5, h = 1;
    const tri = (x: number, y: number, z: number, shade: number) => { pos.push(x, y, z); col.push(shade, shade, shade); };
    // a tapered quad as two triangles, the tip leaning
    tri(-w * c, 0, -w * s, 0.55); tri(w * c, 0, w * s, 0.55); tri(w * 0.3 * c + lean * s, h, w * 0.3 * s - lean * c, 1.0);
    tri(-w * c, 0, -w * s, 0.55); tri(w * 0.3 * c + lean * s, h, w * 0.3 * s - lean * c, 1.0); tri(-w * 0.3 * c + lean * s, h, -w * 0.3 * s - lean * c, 1.0);
  };
  blade(0, 0.25); blade(Math.PI / 3, -0.2); blade((2 * Math.PI) / 3, 0.15);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.computeVertexNormals();
  return g;
}
/** A squat lump, lighter on top. */
function lumpGeometry(detail: number, squash: number, amount: number): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, detail);
  const p = g.attributes.position as THREE.BufferAttribute, col: number[] = [];
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const h = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453, j = 1 + amount * ((h - Math.floor(h)) - 0.5) * 2;
    p.setXYZ(i, x * j, Math.max(0, y * j * squash + 0.15), z * j);
  }
  g.computeVertexNormals();
  for (let i = 0; i < p.count; i++) { const s = 0.7 + 0.3 * Math.max(0, Math.min(1, p.getY(i))); col.push(s, s, s); }
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}
/** A cut stump: a short tapered cylinder, the cut face lighter. */
function stumpGeometry(): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(0.5, 0.62, 1, 7);
  g.translate(0, 0.5, 0);
  const p = g.attributes.position as THREE.BufferAttribute, col: number[] = [];
  for (let i = 0; i < p.count; i++) { const top = p.getY(i) > 0.99; const s = top ? 1.15 : 0.75 + 0.25 * p.getY(i); col.push(s, s, s); }
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}
/** A fallen trunk lying along x: a thin six-sided log a few lengths long, darker underneath. */
function deadfallGeometry(): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(0.42, 0.5, 4.2, 6);
  g.rotateZ(Math.PI / 2); g.translate(0, 0.42, 0);
  const p = g.attributes.position as THREE.BufferAttribute, col: number[] = [];
  for (let i = 0; i < p.count; i++) { const s = 0.7 + 0.45 * Math.max(0, Math.min(1, p.getY(i) / 0.84)); col.push(s, s, s); }
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}
/** A puddle or a dry pan: a flat ten-sided disc lifted a hair off the ground. */
function panGeometry(): THREE.BufferGeometry {
  const g = new THREE.CircleGeometry(1, 10);
  g.rotateX(-Math.PI / 2); g.translate(0, 0.06, 0);
  const p = g.attributes.position as THREE.BufferAttribute, col: number[] = [];
  for (let i = 0; i < p.count; i++) { const edge = Math.hypot(p.getX(i), p.getZ(i)); col.push(1 - 0.25 * edge, 1 - 0.25 * edge, 1 - 0.25 * edge); }
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}
const GEOMETRY: Record<ScatterKind, () => THREE.BufferGeometry> = {
  grass: bladesGeometry, dryGrass: bladesGeometry, flower: bladesGeometry,
  scrub: () => lumpGeometry(1, 0.55, 0.18), stone: () => lumpGeometry(0, 0.6, 0.22),
  stump: stumpGeometry, deadfall: deadfallGeometry, pan: panGeometry,
};

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _up = new THREE.Vector3(), _c = new THREE.Color(), _ca = new THREE.Color(), _cb = new THREE.Color();
const _Y = new THREE.Vector3(0, 1, 0), _yaw = new THREE.Quaternion();

/** One piece placed in the world: its ground point, the ground's normal there, a turn, a size factor and a tint. */
export interface PlacedPiece { pos: Vec3; up: Vec3; yaw: number; scale: number; tint: number; /** 67.50 (1): the drawn relief's lift under it (world; flattened by a prop near) */ lift?: number }

/** How hard the wind bends the grass in each weather (0 still … 1 a gale); without weather, the breeze. */
export const WIND: Record<WeatherKind | 'none', number> = { none: 0.12, clear: 0.12, haze: 0.14, fog: 0.05, rain: 0.35, ash: 0.2, dust: 0.6, storm: 1 };
/** The bend at full wind (a blade's tip moves this fraction of its height) and the gusts' fronts' spacing (people) — 67.36: windSway.ts's, where their pace now follows the wind. */
export const SWAY = { bend: WIND_SWAY.bend, gust: WIND_SWAY.frontPeople };
/** Puddles: wet in rain and storm; seconds to soak and to dry. */
export const WET = { kinds: ['rain', 'storm'] as WeatherKind[], soakS: 15, dryS: 60 };
/** The pans' look dry (white: their own colours, matte) and wet (darker, bluish, a sheen). */
const PAN_DRY = new THREE.Color('#ffffff'), PAN_WET = new THREE.Color('#7b8794'), SHEEN_WET = new THREE.Color('#7d8a96');

/** One step of the ground's wetness (0 dry … 1 soaked) toward the weather: it soaks fast and dries slowly. Pure. */
export function wetnessStep(wet: number, kind: WeatherKind | null, dt: number): number {
  const raining = kind !== null && WET.kinds.includes(kind);
  return raining ? Math.min(1, wet + dt / WET.soakS) : Math.max(0, wet - dt / WET.dryS);
}

/** The wind uniforms the swaying kinds share (GroundScatter owns them; the sheet may pass its own) — 67.36: windSway.ts's, the trees' too. */
export type SwayUniforms = WindSwayUniforms;

/**
 * Bend the blades in the wind (the shader, after `begin_vertex`): each vertex moves along the wind's direction
 * on the ground there, by its height squared (the root stays put, the tip moves most) — the wind's steady lean and its
 * gusts' fronts rolling downwind (67.36, windSway.ts), with a little flutter per blade; a blade bent by a front shows its
 * paler side. Instanced meshes only.
 */
export function swayMaterial(material: THREE.Material, u: SwayUniforms): void {
  // (after an earlier hook, chained — the streamed scatter's shrink and lift: the sway runs first in the shader, so the
  // shrink takes the bent blade whole)
  const prev = material.onBeforeCompile !== THREE.Material.prototype.onBeforeCompile ? material.onBeforeCompile : null;
  const prevKey = prev ? material.customProgramCacheKey.bind(material) : null;
  material.onBeforeCompile = (shader, renderer) => {
    prev?.call(material, shader, renderer);
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', `uniform float uTime;\n${WIND_SWAY_HEAD}varying float vWindBend;\n${WIND_FRONT_GLSL}\nvoid main() {`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
  vWindBend = 0.0;
#ifdef USE_INSTANCING
  {
    vec3 up = normalize(instanceMatrix[1].xyz);
    vec3 w = uWindDir - up * dot(uWindDir, up);
    w = dot(w, w) > 1e-6 ? normalize(w) : normalize(instanceMatrix[0].xyz);
    vec3 base = instanceMatrix[3].xyz;
    float jitter = fract(sin(dot(base, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
    float push = uWind * (${(1 - WIND_SWAY.front).toFixed(3)} + ${WIND_SWAY.front.toFixed(3)} * windFront(base, up, w, jitter) + 0.12 * sin(uTime * 7.0 + jitter * 20.0));
    vec3 wl = normalize(transpose(mat3(instanceMatrix)) * w);
    transformed += wl * (push * ${WIND_SWAY.bend.toFixed(3)} * position.y * position.y);
    vWindBend = clamp(uWind * 1.6, 0.0, 1.0) * windFront(base, up, w, jitter);   // (a front's share: its blades show their paler side)
  }
#endif`);
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'varying float vWindBend;\nvoid main() {')
      .replace('#include <color_fragment>', `#include <color_fragment>\n  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(${WIND_SWAY.pale.map(v => v.toFixed(3)).join(', ')}), ${WIND_SWAY.sheen.toFixed(3)} * vWindBend);   // (67.36: a bent blade's paler side)`);
  };
  material.customProgramCacheKey = () => (prevKey ? `${prevKey()}|scatter-sway` : 'scatter-sway');
}

/**
 * The streamed scatter's shrink: from the eye, whole within `uScatterReach.x`, nothing by `.y` (world); 69.76: with
 * `uScatterGrow` (clock, growS: seconds), each piece grown in from its cell's making (the `aScatterBorn` attribute). Chained.
 */
export function shrinkMaterial(material: THREE.Material, u: { uScatterReach: { value: THREE.Vector2 }; uScatterGrow?: { value: THREE.Vector2 } }): void {
  const prev = material.onBeforeCompile, prevKey = material.customProgramCacheKey.bind(material);
  material.onBeforeCompile = (shader, renderer) => {
    prev.call(material, shader, renderer);
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', `uniform vec2 uScatterReach;\n${u.uScatterGrow ? 'uniform vec2 uScatterGrow;\nattribute float aScatterBorn;\n' : ''}void main() {`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
#ifdef USE_INSTANCING
  transformed *= 1.0 - smoothstep(uScatterReach.x, uScatterReach.y, distance(instanceMatrix[3].xyz, cameraPosition));
${u.uScatterGrow ? '  transformed *= smoothstep(0.0, uScatterGrow.y, uScatterGrow.x - aScatterBorn);' : ''}
#endif`);
  };
  material.customProgramCacheKey = () => `${prevKey()}|scatter-shrink${u.uScatterGrow ? '-grow' : ''}`;
}

/**
 * 67.50 (1) (the owner: "pre placed objects get eaten by fine terrain"): each piece rides the drawn fine ground — its lift
 * (the relief under it, flattened by a prop near: rendering/fineFlatten.ts) × the patch's fade squared, the shader's own
 * (rendering/fineTerrainPatch.ts FINE_FADE: full near the focus, none past the fade or with no patch), along its up.
 * 67.50 (3): with the far lift × (1 − the fade²) (rendering/groundLift.ts), as the patch's vertices ride it. Chained after
 * any other vertex hook (the sway).
 */
export function fineLiftMaterial(material: THREE.Material): void {
  const prev = material.onBeforeCompile, prevKey = material.customProgramCacheKey?.bind(material);
  material.onBeforeCompile = (shader, renderer) => {
    prev?.call(material, shader, renderer);
    Object.assign(shader.uniforms, FINE_FADE, GROUND_LIFT);
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'attribute float aFineLift;\nuniform vec3 uFineFocus;\nuniform vec2 uFineFade;\nuniform float uFineOn;\nuniform float uGroundLift;\nvoid main() {')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
#ifdef USE_INSTANCING
  {
    float f = uFineOn * (1.0 - smoothstep(uFineFade.x, uFineFade.y, distance(instanceMatrix[3].xyz, uFineFocus)));
    transformed.y += (uGroundLift * uFineOn * (1.0 - f * f) + aFineLift * f * f) / length(instanceMatrix[1].xyz);
  }
#endif`);
  };
  material.customProgramCacheKey = () => `${prevKey ? prevKey() : ''}|fine-lift`;
}

/** One instanced mesh for `pieces` of a kind, at the person's height (the sheets use it too). Caller adds it to a scene. */
/** The kinds that sway in the wind. */
export const SWAYING: ScatterKind[] = ['grass', 'dryGrass', 'flower'];

/** A placed piece's matrix (a shared scratch: copy it out). */
function pieceMatrix(kind: ScatterKind, piece: PlacedPiece, person: number): THREE.Matrix4 {
  _p.set(piece.pos.x, piece.pos.y, piece.pos.z);
  _up.set(piece.up.x, piece.up.y, piece.up.z).normalize();
  _q.setFromUnitVectors(_Y, _up).multiply(_yaw.setFromAxisAngle(_Y, piece.yaw));
  _s.setScalar(LOOK[kind].size * person * piece.scale);
  return _m.compose(_p, _q, _s);
}
/** A piece's tint between its kind's two colours (a shared scratch). */
function pieceColor(kind: ScatterKind, tint: number): THREE.Color {
  _ca.set(LOOK[kind].a); _cb.set(LOOK[kind].b);
  return _c.copy(_ca).lerp(_cb, tint);
}

export function scatterMesh(kind: ScatterKind, pieces: readonly PlacedPiece[], person: number, sway?: SwayUniforms): THREE.InstancedMesh {
  const geometry = GEOMETRY[kind]();
  const blades = SWAYING.includes(kind);
  // pans are Phong, so a wet one can take a sheen; everything else matte Lambert
  const material = markShared(kind === 'pan'
    ? new THREE.MeshPhongMaterial({ vertexColors: true, flatShading: true, specular: 0x000000, shininess: 70 })
    : new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, side: blades ? THREE.DoubleSide : THREE.FrontSide }));
  if (blades && sway) swayMaterial(material, sway);
  const lifted = pieces.some(p => p.lift !== undefined);
  if (lifted) fineLiftMaterial(material);
  const mesh = new THREE.InstancedMesh(geometry, material, pieces.length);
  mesh.name = `scatter:${kind}`;
  if (lifted) geometry.setAttribute('aFineLift', new THREE.InstancedBufferAttribute(Float32Array.from(pieces, p => p.lift ?? 0), 1));
  mesh.frustumCulled = false;
  mesh.userData.noBake = true;
  pieces.forEach((piece, i) => {
    mesh.setMatrixAt(i, pieceMatrix(kind, piece, person));
    mesh.setColorAt(i, pieceColor(kind, piece.tint));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

/** A streamed cell's pieces, per kind: matrices, colours and (with the fine relief) lifts. */
interface StreamCell { kinds: Map<ScatterKind, { mats: Float32Array; cols: Float32Array; lifts: Float32Array | null; n: number; /** 69.24: its pieces, to seat again */ pieces: ScatterPiece[] }>; pi: number; /** 69.76: when it was made (the scatter's clock) */ born: number }

/**
 * 69.24: a piece's ground — the coarse radius it stands on and the fine ground's lift over it there as the sim has it: the
 * relief, its bowls (a dig's, a craterlet's: 63.11) and its terraces, flattened by a prop near. A piece took the relief alone
 * and hung over a burst's bowl (the owner: "plants and stuff can get projected higher up … usually in a place where some
 * explosions have happened"). `lift` undefined without fine ground.
 */
function pieceGround(planet: PlanetState, d: Vec3, ps: { fineAmplitude: number; fineWavelength: number; k: number }, discs: ReturnType<typeof flatDiscs>, person: number): { r: number; lift?: number } {
  const r = getSurfaceRadius(planet, d);
  if (!(ps.fineAmplitude > 0)) return { r };
  return { r, lift: fineGroundLift(planet, d, ps, ps.k, r) * flatFactor(planet, d, discs, person) };
}

/** 69.24: how far a moved coarse vertex reaches (world): the planet's longest triangle edge (the mesh is non-indexed: triangle t is entries 3t … 3t + 2). */
const facetMemo = new WeakMap<object, number>();
function facetReach(planet: Pick<PlanetState, 'basePositions'>): number {
  const b = planet.basePositions;
  let f = facetMemo.get(b);
  if (f !== undefined) return f;
  f = 0;
  for (let t = 0; t + 8 < b.length; t += 9) for (const [u, v] of [[0, 3], [3, 6], [6, 0]]) f = Math.max(f, Math.hypot(b[t + u] - b[t + v], b[t + u + 1] - b[t + v + 1], b[t + u + 2] - b[t + v + 2]));
  facetMemo.set(b, f);
  return f;
}

/** 69.24: what of a planet's ground the scatter last seated on (by identity: each edit replaces its list or its heightmap). */
interface GroundSeen { heightmap: PlanetState['heightmap']; craterlets: PlanetState['craterlets']; digs: PlanetState['digs']; terraces: PlanetState['terraces'] }

export class GroundScatter {
  readonly meshes: THREE.InstancedMesh[] = [];
  readonly count: number;
  /** the wind the swaying kinds share; the ground's wetness (0 dry … 1 soaked) */
  readonly sway: SwayUniforms;
  wetness = 0;
  private pans: THREE.MeshPhongMaterial | null = null;
  /** 62.61's follow-up: the forest cells' clutter, streamed (`streamForest`) — one mesh per kind, the cells held by key */
  private readonly streamMeshes = new Map<ScatterKind, THREE.InstancedMesh>();
  private readonly streamCells = new Map<string, StreamCell>();
  private streamDirty = false;
  private repackMs = 0;
  private disposed = false;
  private readonly reachU = { uScatterReach: { value: new THREE.Vector2() }, uScatterGrow: { value: new THREE.Vector2(0, SCATTER_STREAM.growS) } };
  private readonly person: number;
  /** 67.36: a person's height in world units, at either scale (the fronts' spacing and pace) */
  private readonly swayPerson: number;
  private readonly fine: { amplitude: number; wavelength: number };
  private readonly discs = new Map<number, ReturnType<typeof flatDiscs>>();
  /** 69.24: the static pieces by mesh (their plan and their placing), and each planet's ground as they were last seated on it */
  private readonly statics: Array<{ mesh: THREE.InstancedMesh; kind: ScatterKind; pieces: ScatterPiece[]; placed: PlacedPiece[] }> = [];
  private readonly seen = new Map<number, GroundSeen>();
  private readonly ps: { fineAmplitude: number; fineWavelength: number; k: number };

  constructor(private readonly scene: THREE.Scene, private readonly state: GameState, seed: string) {
    const plan = scatterPlan(state, seed);
    this.count = plan.length;
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    this.person = ps.k < 1 ? person : 0;   // (human scale only, as the plan)
    this.fine = { amplitude: ps.fineAmplitude, wavelength: ps.fineWavelength };
    this.ps = { fineAmplitude: ps.fineAmplitude, fineWavelength: ps.fineWavelength, k: ps.k };
    state.planets.forEach((pl, i) => { this.seen.set(i, { heightmap: pl.heightmap, craterlets: pl.craterlets, digs: pl.digs, terraces: pl.terraces }); facetReach(pl); });   // (the reach worked out here, not at the first crater: ~1 ms a planet)
    // the wind's direction is the match's (seeded), its gusts a few people long
    const a = new SeededRandom(`${seed}-wind`).range(0, Math.PI * 2);
    this.sway = windSwayUniforms(person, new THREE.Vector3(Math.cos(a), 0.35, Math.sin(a)), WIND.none);
    this.swayPerson = person;
    for (const kind of SCATTER_KINDS) {
      const pieces = plan.filter(p => p.kind === kind);
      if (pieces.length === 0) continue;
      const placed: PlacedPiece[] = pieces.map(piece => {
        const planet: PlanetState = state.planets[piece.planetIndex];
        // (69.24: its ground as pieceGround has it — the relief, the bowls and the terraces under it — and seated again when that
        // ground changes, reground)
        const g = pieceGround(planet, piece.dir, this.ps, flatDiscs(state, piece.planetIndex), person), r = g.r, lift = g.lift;
        return { pos: { x: planet.position.x + piece.dir.x * r, y: planet.position.y + piece.dir.y * r, z: planet.position.z + piece.dir.z * r }, up: piece.dir, yaw: piece.yaw, scale: piece.scale, tint: piece.tint, ...(lift !== undefined ? { lift } : {}) };
      });
      const mesh = scatterMesh(kind, placed, person, this.sway);
      if (kind === 'pan') this.pans = mesh.material as THREE.MeshPhongMaterial;
      scene.add(mesh);
      this.statics.push({ mesh, kind, pieces, placed });
      this.meshes.push(mesh);
    }
  }

  /**
   * Each frame: the weather on the ground (`kind`, null without weather) — the wind eases toward the kind's
   * (over a couple of seconds), the gusts run on, and the pans soak or dry (`wetnessStep`). 67.36: `field` is the wind
   * where the viewer is with decision 120's switch on (its way along the ground and its level against a storm's): the
   * grass then bends the way the field blows, as hard as it blows there; without it, the weather's breeze along the
   * match's way.
   */
  weather(kind: WeatherKind | null, dt: number, windFactor = 1, field?: { dir: { x: number; y: number; z: number } | null; level: number }): void {
    this.flush();   // (and the streamed cells, repacked when the near field made or dropped one)
    this.reachU.uScatterGrow.value.x += dt;   // 69.76: the clock the streamed cells grow in by
    const want = field?.dir ? field.level : WIND[kind ?? 'none'] * windFactor;   // 62.47: the gust and the shelter where the viewer is
    stepWindSway(this.sway, field?.dir ?? null, want, dt, this.swayPerson);
    this.wetness = wetnessStep(this.wetness, kind, dt);
    if (this.pans) {
      this.pans.color.copy(PAN_DRY).lerp(PAN_WET, this.wetness);
      this.pans.specular.setRGB(0, 0, 0).lerp(SHEEN_WET, this.wetness);
    }
  }

  /**
   * 62.61's follow-up: stream the forest cells' clutter through the near field — a client on the forest's grid, its reach
   * SCATTER_STREAM.reachPeople, each cell's pieces from the trees the stands filed for it (`source`). Human scale only.
   */
  streamForest(streamer: NearFieldStreamer, source: ForestScatterSource): void {
    const grid = source.forestGrid();
    if (!grid.length || this.person <= 0) return;
    const nOf = new Map(grid.map(g => [g.planetIndex, g.n]));
    const reach = SCATTER_STREAM.reachPeople * this.person;
    this.reachU.uScatterReach.value.set(reach * SCATTER_STREAM.shrinkFrom, reach);
    const client: NearFieldClient<string> = {
      name: 'scatter',
      planets: () => grid.map(g => g.planetIndex),
      cellsPerEdge: pi => nOf.get(pi)!,
      reach: () => reach,
      make: (pi, planet, cell) => this.makeCell(pi, planet, cell, source),
      evict: (_pi, _cell, key) => { if (key !== null && this.streamCells.delete(key)) this.streamDirty = true; },
    };
    streamer.register(client);
  }

  /** A forest cell's pieces placed on its ground (the relief's lift beside each), held by key; null when it has none. */
  private makeCell(pi: number, planet: PlanetState, cell: number, source: ForestScatterSource): string | null {
    if (this.disposed) return null;
    const src = source.forestCellOf(pi, planet, cell);
    if (!src || !src.trees.length) return null;
    const pieces = forestScatterCell(pi, src.seed, cell, src.trees, src.keepOut, this.person, planet.radius);
    if (!pieces.length) return null;
    let discs = this.discs.get(pi);
    if (!discs) { discs = flatDiscs(this.state, pi); this.discs.set(pi, discs); }
    const counts = new Map<ScatterKind, number>();
    for (const p of pieces) counts.set(p.kind, (counts.get(p.kind) ?? 0) + 1);
    const lifted = this.fine.amplitude > 0;
    const sc: StreamCell = { kinds: new Map(), pi, born: this.reachU.uScatterGrow.value.x };
    for (const [kind, n] of counts) sc.kinds.set(kind, { mats: new Float32Array(n * 16), cols: new Float32Array(n * 3), lifts: lifted ? new Float32Array(n) : null, n: 0, pieces: [] });
    for (const piece of pieces) {
      const k = sc.kinds.get(piece.kind)!, d = piece.dir, g = pieceGround(planet, d, this.ps, discs, this.person), r = g.r;
      k.pieces.push(piece);
      const placed: PlacedPiece = { pos: { x: planet.position.x + d.x * r, y: planet.position.y + d.y * r, z: planet.position.z + d.z * r }, up: d, yaw: piece.yaw, scale: piece.scale, tint: piece.tint };
      pieceMatrix(piece.kind, placed, this.person).toArray(k.mats, k.n * 16);
      pieceColor(piece.kind, piece.tint).toArray(k.cols, k.n * 3);
      if (k.lifts) k.lifts[k.n] = g.lift ?? 0;
      k.n++;
    }
    const key = `${pi}:${cell}`;
    this.streamCells.set(key, sc);
    this.streamDirty = true;
    return key;
  }

  /**
   * 69.24: each frame, the planets as they stand now — where a planet's ground has changed since the pieces were seated (a crater
   * moved the coarse mesh's vertices, a burst left a craterlet or deepened one, a dig went down, a terrace was laid), the pieces
   * on the changed ground are seated again: their coarse radius and their lift. A moved vertex changes its triangles' ground out
   * to a facet (tens of people at the human scale); a bowl, its reach. Nothing when nothing changed (identity checks).
   */
  reground(planets: readonly PlanetState[]): void {
    if (this.disposed || !(this.person > 0)) return;
    planets.forEach((planet, pi) => {
      const was = this.seen.get(pi);
      if (!was || (was.heightmap === planet.heightmap && was.craterlets === planet.craterlets && was.digs === planet.digs && was.terraces === planet.terraces)) return;
      this.seen.set(pi, { heightmap: planet.heightmap, craterlets: planet.craterlets, digs: planet.digs, terraces: planet.terraces });
      const R = planet.radius, caps: Array<{ x: number; y: number; z: number; cos: number }> = [];
      const cap = (d: Vec3, reach: number) => { const l = Math.hypot(d.x, d.y, d.z); caps.push({ x: d.x / l, y: d.y / l, z: d.z / l, cos: dcos(Math.min(Math.PI, reach / R)) }); };
      // the coarse vertices a crater moved: their triangles, out to a facet — one cap round them all (their mean, out to the
      // farthest and a facet more; a cap a vertex cost 7 ms a crater against every piece)
      if (was.heightmap !== planet.heightmap && was.heightmap.length === planet.heightmap.length) {
        const b = planet.basePositions, moved: Vec3[] = [];
        for (let i = 0; i < planet.heightmap.length; i++) if (planet.heightmap[i] !== was.heightmap[i]) { const x = b[i * 3], y = b[i * 3 + 1], z = b[i * 3 + 2], l = Math.hypot(x, y, z); moved.push({ x: x / l, y: y / l, z: z / l }); }
        if (moved.length) {
          const m = moved.reduce((s, d) => ({ x: s.x + d.x, y: s.y + d.y, z: s.z + d.z }), { x: 0, y: 0, z: 0 }), ml = Math.hypot(m.x, m.y, m.z);
          const c = ml > 1e-9 ? { x: m.x / ml, y: m.y / ml, z: m.z / ml } : moved[0];
          const far = moved.reduce((w, d) => Math.min(w, d.x * c.x + d.y * c.y + d.z * c.z), 1);
          cap(c, dacos(Math.max(-1, Math.min(1, far))) * R + facetReach(planet));
        }
      }
      // the bowls and the pads added, changed or gone (by identity: a deepened craterlet, a dig's progress, a re-laid terrace)
      const bowls = <T extends { id: number; dir: Vec3 }>(a: readonly T[] | undefined, b: readonly T[] | undefined, reach: (x: T) => number) => {
        const before = new Map((a ?? []).map(x => [x.id, x] as const)), after = new Map((b ?? []).map(x => [x.id, x] as const));
        for (const [id, x] of after) if (before.get(id) !== x) cap(x.dir, reach(x));
        for (const [id, x] of before) if (after.get(id) !== x) cap(x.dir, reach(x));
      };
      bowls(was.craterlets, planet.craterlets, () => CRATERLET_REACH * this.person);
      bowls(was.digs, planet.digs, () => 3 * this.person);
      bowls(was.terraces, planet.terraces, t => terraceReach(t) + facetReach(planet));
      if (!caps.length) return;
      const hit = (d: Vec3) => { const l = Math.hypot(d.x, d.y, d.z); return caps.some(c => (d.x * c.x + d.y * c.y + d.z * c.z) / l > c.cos); };
      let discs = this.discs.get(pi);
      if (!discs) { discs = flatDiscs(this.state, pi); this.discs.set(pi, discs); }
      // the static pieces
      for (const st of this.statics) {
        let moved = false;
        const lifts = st.mesh.geometry.getAttribute('aFineLift') as THREE.InstancedBufferAttribute | undefined;
        st.pieces.forEach((piece, i) => {
          if (piece.planetIndex !== pi || !hit(piece.dir)) return;
          const g = pieceGround(planet, piece.dir, this.ps, discs!, this.person);
          const placed = { ...st.placed[i], pos: { x: planet.position.x + piece.dir.x * g.r, y: planet.position.y + piece.dir.y * g.r, z: planet.position.z + piece.dir.z * g.r }, ...(g.lift !== undefined ? { lift: g.lift } : {}) };
          st.placed[i] = placed;
          st.mesh.setMatrixAt(i, pieceMatrix(st.kind, placed, this.person));
          if (lifts && g.lift !== undefined) lifts.setX(i, g.lift);
          moved = true;
        });
        if (moved) { st.mesh.instanceMatrix.needsUpdate = true; if (lifts) lifts.needsUpdate = true; }
      }
      // the streamed cells (refilled from their arrays by flush)
      for (const sc of this.streamCells.values()) {
        if (sc.pi !== pi) continue;
        for (const [kind, k] of sc.kinds) {
          k.pieces.forEach((piece, i) => {
            if (!hit(piece.dir)) return;
            const g = pieceGround(planet, piece.dir, this.ps, discs!, this.person);
            const placed: PlacedPiece = { pos: { x: planet.position.x + piece.dir.x * g.r, y: planet.position.y + piece.dir.y * g.r, z: planet.position.z + piece.dir.z * g.r }, up: piece.dir, yaw: piece.yaw, scale: piece.scale, tint: piece.tint };
            pieceMatrix(kind, placed, this.person).toArray(k.mats, i * 16);
            if (k.lifts) k.lifts[i] = g.lift ?? 0;
            this.streamDirty = true;
          });
        }
      }
    });
  }

  /** The streamed meshes refilled from the held cells (in the order they were made), each grown when it lacks room. */
  flush(): void {
    if (!this.streamDirty || this.disposed) return;
    this.streamDirty = false;
    const t0 = performance.now();
    const totals = new Map<ScatterKind, number>();
    for (const c of this.streamCells.values()) for (const [kind, k] of c.kinds) totals.set(kind, (totals.get(kind) ?? 0) + k.n);
    for (const [kind, mesh] of this.streamMeshes) if (!totals.has(kind)) mesh.count = 0;
    for (const [kind, total] of totals) {
      let mesh = this.streamMeshes.get(kind);
      if (!mesh || mesh.instanceMatrix.count < total) mesh = this.streamMesh(kind, Math.max(total, Math.ceil((mesh?.instanceMatrix.count ?? 0) * 1.5), 256), mesh);
      const mats = mesh.instanceMatrix.array as Float32Array, cols = mesh.instanceColor!.array as Float32Array;
      const lift = mesh.geometry.getAttribute('aFineLift') as THREE.InstancedBufferAttribute | undefined;
      const born = mesh.geometry.getAttribute('aScatterBorn') as THREE.InstancedBufferAttribute, bornAt = born.array as Float32Array;
      let n = 0;
      for (const c of this.streamCells.values()) {
        const k = c.kinds.get(kind);
        if (!k) continue;
        mats.set(k.mats, n * 16); cols.set(k.cols, n * 3);
        if (lift && k.lifts) (lift.array as Float32Array).set(k.lifts, n);
        bornAt.fill(c.born, n, n + k.n);
        n += k.n;
      }
      mesh.count = n;
      mesh.instanceMatrix.needsUpdate = true; mesh.instanceColor!.needsUpdate = true; born.needsUpdate = true;
      if (lift) lift.needsUpdate = true;
    }
    this.repackMs += performance.now() - t0;
  }

  /** A streamed kind's mesh with room for `capacity` (its material kept from `old`, which it replaces in the scene). */
  private streamMesh(kind: ScatterKind, capacity: number, old?: THREE.InstancedMesh): THREE.InstancedMesh {
    let material = old?.material as THREE.MeshLambertMaterial | undefined;
    if (!material) {
      material = markShared(new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, side: SWAYING.includes(kind) ? THREE.DoubleSide : THREE.FrontSide }));
      // (chained so the shader bends the blade, shrinks it whole, then lifts it onto the drawn relief)
      if (this.fine.amplitude > 0) fineLiftMaterial(material);
      shrinkMaterial(material, this.reachU);
      if (SWAYING.includes(kind)) swayMaterial(material, this.sway);
    }
    const geometry = old?.geometry ?? GEOMETRY[kind]();
    if (this.fine.amplitude > 0) geometry.setAttribute('aFineLift', new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('aScatterBorn', new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1).setUsage(THREE.DynamicDrawUsage));   // (69.76; refilled by flush)
    const mesh = new THREE.InstancedMesh(geometry, material, capacity);
    mesh.name = `scatter:${kind}:streamed`;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3).setUsage(THREE.DynamicDrawUsage);
    mesh.count = 0;
    mesh.frustumCulled = false;   // (it spans the near field round the eye)
    mesh.userData.noBake = true;
    if (old) { this.scene.remove(old); old.dispose(); }
    this.scene.add(mesh);
    this.streamMeshes.set(kind, mesh);
    return mesh;
  }

  /** The streamed scatter now: cells held, pieces drawn, the meshes' room, and the repacks' time so far (ms). */
  streamStats(): { cells: number; pieces: number; capacity: number; meshes: number; repackMs: number } {
    let pieces = 0, capacity = 0;
    for (const m of this.streamMeshes.values()) { pieces += m.count; capacity += m.instanceMatrix.count; }
    return { cells: this.streamCells.size, pieces, capacity, meshes: this.streamMeshes.size, repackMs: +this.repackMs.toFixed(2) };
  }

  dispose(): void {
    this.disposed = true;
    for (const m of this.meshes) { this.scene.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose(); m.dispose(); }
    this.meshes.length = 0;
    for (const m of this.streamMeshes.values()) { this.scene.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose(); m.dispose(); }
    this.streamMeshes.clear();
    this.streamCells.clear();
  }
}
